/**
 * Page 4: Your Focus
 *
 * Personalized focus areas (Movement, Nutrition, Recovery) with actionable suggestions.
 * Feature: 017-body-insight-summary
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BodyInsightProgress } from '../../components/body-insight/BodyInsightProgress';
import { getPageContent } from '../../services/bodyInsightTemplates';

// Emoji mapping for focus area categories
const CATEGORY_EMOJIS: { [key: string]: string } = {
  'Movement': '🏃‍♀️',
  'Nutrition': '🥗',
  'Recovery': '😴'
};

export const Page4YourFocus: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get personalized content for this page
  const content = user ? getPageContent(4, user.profile as any) : null;

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#FFF5E9] to-[#F8F4EF]">
        <p className="text-gray-600">Unable to load personalized content.</p>
      </div>
    );
  }

  const handleBack = () => {
    navigate('/body-insight/3');
  };

  const handleNext = () => {
    navigate('/body-insight/5');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5E9] to-[#F8F4EF] py-8 px-4">
      <div className="max-w-[800px] mx-auto">
        {/* Progress indicator */}
        <BodyInsightProgress currentPage={4} totalPages={5} />

        {/* Main content card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          {/* Headline */}
          <p className="text-sm uppercase tracking-wide text-[#B7A76D] mb-2 font-semibold">
            Here's what we understand about you
          </p>

          {/* Page title */}
          <h1 className="font-['Nunito'] text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            YOUR FOCUS 🎯
          </h1>

          {/* Introduction */}
          {content.headline && (
            <h2 className="font-['Nunito'] text-2xl font-semibold text-[#C98CCC] mb-4">
              {content.headline}
            </h2>
          )}

          {content.body && (
            <p className="font-['Inter'] text-lg leading-relaxed text-gray-700 mb-8">
              {content.body}
            </p>
          )}

          {/* Focus Area Cards */}
          {content.focusAreas && content.focusAreas.length > 0 && (
            <div className="space-y-6">
              {content.focusAreas.map((focusArea, index) => (
                <div
                  key={index}
                  className="border border-[#E5D5C3] rounded-lg shadow-sm p-6 bg-white hover:shadow-md transition-shadow duration-200"
                >
                  {/* Category header with emoji */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">
                      {CATEGORY_EMOJIS[focusArea.category] || '✨'}
                    </span>
                    <div>
                      <h3 className="font-['Nunito'] text-xl font-bold text-gray-900">
                        {focusArea.title}
                      </h3>
                      <p className="text-sm font-semibold text-[#B7A76D] uppercase tracking-wide">
                        {focusArea.category}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="font-['Inter'] text-base text-gray-700 mb-3 leading-relaxed">
                    {focusArea.description}
                  </p>

                  {/* Why this matters */}
                  <div className="bg-[#FFF5E9] rounded-lg p-4 mb-4">
                    <p className="font-['Inter'] text-sm text-gray-600">
                      <span className="font-semibold text-[#6B5D4F]">Why this matters:</span>{' '}
                      {focusArea.why}
                    </p>
                  </div>

                  {/* Actionable suggestions */}
                  {focusArea.suggestions && focusArea.suggestions.length > 0 && (
                    <div>
                      <p className="font-['Inter'] text-sm font-semibold text-gray-600 mb-2">
                        How to do it:
                      </p>
                      <ul className="space-y-2">
                        {focusArea.suggestions.map((suggestion, i) => (
                          <li
                            key={i}
                            className="flex items-start font-['Inter'] text-sm text-[#6B5D4F]"
                          >
                            <span className="text-[#B7A76D] mr-2 mt-0.5">•</span>
                            <span className="leading-relaxed">{suggestion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Medical disclaimer */}
        <div className="bg-white/50 rounded-lg p-4 mb-6 border border-gray-200">
          <p className="text-sm text-gray-600 text-center font-['Inter']">
            💡 This guidance is educational and does not replace medical advice
          </p>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleBack}
            className="bg-white text-[#B7A76D] font-semibold py-3 px-8 rounded-full border-2 border-[#B7A76D] hover:bg-[#B7A76D] hover:text-white transition-all duration-200 transform hover:scale-105"
          >
            Back
          </button>
          <button
            onClick={handleNext}
            className="bg-gradient-to-r from-[#C98CCC] to-[#B7A76D] text-white font-semibold py-3 px-8 rounded-full hover:shadow-lg transition-all duration-200 transform hover:scale-105"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Page4YourFocus;
